/**
 * LES VOCABULAIRES DE L'ACTE COMMUNAL — un seul jeu, trois lecteurs.
 *
 * ⚠️ RANGÉ DANS `src/partage/` POUR LA MÊME RAISON QUE `rubriques.ts` et
 * `pieces-identite.ts` : l'étage `interface` du Dockerfile ne copie que
 * `frontend/` et `src/partage/`. Un vocabulaire rangé dans `frontend/src/`
 * compilerait ici et laisserait le SERVICE sans la liste qu'il doit valider.
 *
 * ⚠️⚠️ ET CE FICHIER EXISTE PARCE QUE LA FAUTE A DÉJÀ ÉTÉ COMMISE, LE MÊME JOUR.
 * Un menu de pièces d'identité inventé (`carte_identite`, `attestation_identite`)
 * a fait échouer un enrôlement APRÈS l'émission de l'invitation — donc après
 * qu'un code ait été remis à un agent. La contrainte de la base ne connaissait
 * ni l'une ni l'autre. Ici, les deux listes sont les énumérations PostgreSQL
 * `fon.role_attestation` et `fon.origine_propriete`, recopiées SOUS GARDE :
 * `tests/attestation.test.ts` les extrait de `pg_enum` et les confronte DANS LES
 * DEUX SENS, avec un témoin négatif. Une valeur inventée rougit avant la
 * production ; une valeur ajoutée en base et pas ici n'est jamais proposée, et
 * l'essai le dit.
 */

/** `fon.role_attestation` — qui figure à l'acte, et à quel titre. */
export const ROLES_ATTESTATION = [
  'cedant', 'temoin_cedant', 'temoin_acquereur', 'chef_secteur', 'president_district',
] as const;
export type RoleAttestation = (typeof ROLES_ATTESTATION)[number];

export const LIBELLE_ROLE_ATTESTATION: Record<RoleAttestation, string> = {
  cedant: 'Cédant',
  temoin_cedant: 'Témoin du cédant',
  temoin_acquereur: "Témoin de l'acquéreur",
  chef_secteur: 'Chef de secteur',
  president_district: 'Président de district',
};

/** `fon.origine_propriete` — d'où le cédant tient le sol. */
export const ORIGINES_PROPRIETE = [
  'heritage', 'achat', 'attribution_coutumiere', 'attribution_administrative',
  'defrichement', 'donation', 'echange', 'autre',
] as const;
export type OriginePropriete = (typeof ORIGINES_PROPRIETE)[number];

export const LIBELLE_ORIGINE: Record<OriginePropriete, string> = {
  heritage: 'Héritage',
  achat: 'Achat',
  attribution_coutumiere: 'Attribution coutumière',
  attribution_administrative: 'Attribution administrative',
  defrichement: 'Défrichement',
  donation: 'Donation',
  echange: 'Échange',
  autre: 'Autre',
};

/** `fon.statut_attestation` — le cycle de l'acte. */
export const STATUTS_ATTESTATION = ['brouillon', 'signee', 'delivree', 'annulee'] as const;
export type StatutAttestation = (typeof STATUTS_ATTESTATION)[number];

export const LIBELLE_STATUT_ATTESTATION: Record<StatutAttestation, string> = {
  brouillon: 'Brouillon',
  signee: 'Signée',
  delivree: 'Délivrée',
  annulee: 'Annulée',
};

/**
 * LA PHRASE QUE L'ACTE PORTE, ET QUI NE SE NÉGOCIE PAS.
 *
 * ⚠️ ELLE EST ICI, PAS DANS LE GABARIT D'IMPRESSION. Un gabarit se remplace ;
 * la phrase qui dit ce que l'acte n'établit pas doit survivre au remplacement.
 * Une attestation présentée comme un titre est le premier vecteur de double
 * vente : deux acquéreurs de bonne foi, chacun avec « son papier de la mairie ».
 */
export const NE_VAUT_PAS_TITRE =
  "Cette attestation NE VAUT PAS TITRE. Elle constate une cession devant témoins ; elle ne "
  + "garantit pas la propriété et ne vaut pas immatriculation. Le titre foncier est délivré par "
  + "la Conservation Foncière, sur une feuille du livre foncier.";
